import React, { useState, useEffect } from 'react'
import { useApi } from '../../contexts/ApiContext'
import { useUserId } from '../../hooks/useUserId'
import { useAuth } from '../../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import CityHospitalsSection from '../../components/CityHospitalsSection'
import { trackDashboardView, openChatWithContext } from '../../utils/eventTracking'
import { 
  CreditCard, 
  FileText, 
  Building2, 
  Plus,
  Home,
  HelpCircle,
  Phone,
  Mail,
  MessageCircle,
  Shield,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  AlertTriangle
} from 'lucide-react'
import { getStatusBadgeColor } from '../../utils/policyCalculations'

const CustomerDashboard = () => {
  // Force refresh to clear any cached permissions
  console.log('CustomerDashboard refreshed at:', new Date().toISOString())
  
  const { getPolicies, getClaims, getHospitals, getUserPolicies, getUserPoliciesWithClaims } = useApi()
  const { userId, hasUserId, isFromUrl } = useUserId()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [policies, setPolicies] = useState([])
  const [claims, setClaims] = useState([])
  const [hospitals, setHospitals] = useState([])
  const [loading, setLoading] = useState(true)
  const [totalPolicies, setTotalPolicies] = useState(0)
  const [totalClaims, setTotalClaims] = useState(0)
  const [totalHospitals, setTotalHospitals] = useState(0)
  const [userCity, setUserCity] = useState('')
  const [locationPermission, setLocationPermission] = useState(null) // null, 'granted', 'denied', 'prompting'
  const [showLocationInput, setShowLocationInput] = useState(false)
  const [manualCity, setManualCity] = useState('')

  // City mapping function to handle variations
  const mapCityName = (sessionCity) => {
    const cityMappings = {
      'Bengaluru': 'Bangalore',
      'Bangalore': 'Bangalore',
      'Mumbai': 'Mumbai',
      'Delhi': 'Delhi',
      'New Delhi': 'Delhi',
      'Kolkata': 'Kolkata',
      'Calcutta': 'Kolkata',
      'Chennai': 'Chennai',
      'Madras': 'Chennai',
      'Hyderabad': 'Hyderabad',
      'Pune': 'Pune',
      'Ahmedabad': 'Ahmedabad',
      'Jaipur': 'Jaipur',
      'Lucknow': 'Lucknow',
      'Kanpur': 'Kanpur',
      'Nagpur': 'Nagpur',
      'Indore': 'Indore',
      'Thane': 'Thane',
      'Bhopal': 'Bhopal',
      'Visakhapatnam': 'Visakhapatnam',
      'Vizag': 'Visakhapatnam',
      'Pimpri-Chinchwad': 'Pune',
      'Patna': 'Patna',
      'Vadodara': 'Vadodara',
      'Ludhiana': 'Ludhiana',
      'Agra': 'Agra',
      'Nashik': 'Nashik',
      'Faridabad': 'Faridabad',
      'Meerut': 'Meerut',
      'Rajkot': 'Rajkot',
      'Kalyan-Dombivali': 'Mumbai',
      'Vasai-Virar': 'Mumbai',
      'Varanasi': 'Varanasi',
      'Srinagar': 'Srinagar',
      'Aurangabad': 'Aurangabad',
      'Navi Mumbai': 'Mumbai',
      'Solapur': 'Solapur',
      'Vijayawada': 'Vijayawada',
      'Kolhapur': 'Kolhapur',
      'Amritsar': 'Amritsar',
      'Noida': 'Delhi',
      'Ghaziabad': 'Delhi',
      'Gurgaon': 'Gurgaon',
      'Gurugram': 'Gurgaon'
    }
    
    return cityMappings[sessionCity] || sessionCity
  }

  // Function to get user's city from geolocation
  const getUserCity = async () => {
    try {
      
      // First try to get from session data
      if (user && user.id) {
        const { data, error } = await supabase
          .from('sessions')
          .select('location_city')
          .eq('user_id', user.id)
          .eq('session_type', 'login')
          .order('login_time', { ascending: false })
          .limit(1)
          .single()

        if (data && data.location_city) {
          const mappedCity = mapCityName(data.location_city)
          setUserCity(mappedCity)
          return
        }
      }

      // Try to get from user metadata
      if (user?.user_metadata?.city) {
        const mappedCity = mapCityName(user.user_metadata.city)
        setUserCity(mappedCity)
        return
      }

      // If no stored location, use browser geolocation
      await getLocationFromBrowser()
      
    } catch (error) {
      console.error('Error getting user city:', error)
      // Fallback to browser geolocation
      await getLocationFromBrowser()
    }
  }

  // Get location from browser geolocation
  const getLocationFromBrowser = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        setUserCity('')
        setLocationPermission('denied')
        resolve()
        return
      }

      setLocationPermission('prompting')
      
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            setLocationPermission('granted')
            const { latitude, longitude } = position.coords
            
            // Use reverse geocoding to get city
            const city = await reverseGeocode(latitude, longitude)
            if (city) {
              const mappedCity = mapCityName(city)
              setUserCity(mappedCity)
            } else {
              setUserCity('')
            }
          } catch (error) {
            console.error('Error processing location:', error)
            setUserCity('')
          }
          resolve()
        },
        (error) => {
          setLocationPermission('denied')
          setUserCity('')
          resolve()
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      )
    })
  }

  // Reverse geocoding to get city from coordinates
  const reverseGeocode = async (latitude, longitude) => {
    try {
      // Use a free reverse geocoding service
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
      )
      
      if (response.ok) {
        const data = await response.json()
        
        // Extract city from the response
        const city = data.city || data.locality || data.principalSubdivision
        return city
      } else {
        return null
      }
    } catch (error) {
      console.error('❌ Reverse geocoding error:', error)
      return null
    }
  }

  // Handle manual city input
  const handleManualCitySubmit = () => {
    if (manualCity.trim()) {
      const mappedCity = mapCityName(manualCity.trim())
      setUserCity(mappedCity)
      setShowLocationInput(false)
      setManualCity('')
    }
  }

  // Retry location detection
  const retryLocationDetection = () => {
    setLocationPermission(null)
    setUserCity('')
    getUserCity()
  }

  useEffect(() => {
    if (hasUserId) {
      loadData()
    }
  }, [hasUserId, userId])

  useEffect(() => {
    if (hasUserId) {
      getUserCity()
    }
  }, [hasUserId, userId])

  // Removed problematic useEffect that was causing nearby hospitals to show 0

  const loadData = async () => {
    try {
      setLoading(true)
      
      console.log('Customer Dashboard - User ID:', userId)
      console.log('Customer Dashboard - Has User ID:', hasUserId)
      console.log('Customer Dashboard - Is from URL:', isFromUrl)
      
      if (userId) {
        // Use the new user policy functions for better performance and security
        const [policiesData, claimsData, hospitalsData] = await Promise.all([
          getUserPolicies(userId),
          getUserPoliciesWithClaims(userId),
          getHospitals() // Hospitals don't need filtering
        ])
        
        // Extract unique policies and claims from the combined data
        const uniquePolicies = policiesData
        const uniqueClaims = claimsData.filter((claim, index, self) => 
          claim.claim_id && self.findIndex(c => c.claim_id === claim.claim_id) === index
        )
        
        // Set total counts for display
        setTotalPolicies(uniquePolicies.length)
        setTotalClaims(uniqueClaims.length)
        setTotalHospitals(hospitalsData.length)
        
        // Track dashboard view with metrics
        trackDashboardView(userId, {
          totalPolicies: uniquePolicies.length,
          totalClaims: uniqueClaims.length,
          totalHospitals: hospitalsData.length
        })
        
        // Calculate nearby hospitals based on user's city
        
        // Set display data (limited for UI)
        setPolicies(uniquePolicies.slice(0, 3)) // Show only first 3
        setClaims(uniqueClaims.slice(0, 3)) // Show only first 3
        
        // Filter hospitals by user's city for display
        if (userCity && hospitalsData) {
          const cityFilteredHospitals = hospitalsData.filter(hospital => {
            if (!hospital.city) return false
            const hospitalCity = hospital.city.toLowerCase().trim()
            const userCityLower = userCity.toLowerCase().trim()
            
            // Check for exact match or partial match
            return hospitalCity === userCityLower || 
                   hospitalCity.includes(userCityLower) || 
                   userCityLower.includes(hospitalCity)
          })
          
          // If we found city-specific hospitals, use them; otherwise show all
          if (cityFilteredHospitals.length > 0) {
            setHospitals(cityFilteredHospitals.slice(0, 6))
          } else {
            setHospitals(hospitalsData.slice(0, 6))
          }
        } else {
          setHospitals(hospitalsData.slice(0, 6)) // Show all if no city
        }
      } else {
        // Fallback to old method if no customer ID
        const [policiesData, claimsData, hospitalsData] = await Promise.all([
          getPolicies(),
          getClaims(),
          getHospitals()
        ])
        
        setTotalPolicies(policiesData.length)
        setTotalClaims(claimsData.length)
        setTotalHospitals(hospitalsData.length)
        
        // Calculate nearby hospitals for fallback
        if (userCity && hospitalsData) {
          const nearbyCount = hospitalsData.filter(hospital => {
            if (!hospital.city) return false
            const hospitalCity = hospital.city.toLowerCase().trim()
            const userCityLower = userCity.toLowerCase().trim()
            
            return hospitalCity === userCityLower || 
                   hospitalCity.includes(userCityLower) || 
                   userCityLower.includes(hospitalCity)
          }).length
          
          // Nearby hospitals count (removed from UI)
        } else {
          // Nearby hospitals count (removed from UI)
        }
        
        setPolicies(policiesData.slice(0, 3))
        setClaims(claimsData.slice(0, 3))
        setHospitals(hospitalsData.slice(0, 6))
        
        console.log('Customer Dashboard - Fallback policies:', policiesData.length)
        console.log('Customer Dashboard - Fallback claims:', claimsData.length)
      }
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  // Get policy status icon
  const getPolicyStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return <CheckCircle className="h-3 w-3" />
      case 'EXPIRED':
      case 'CANCELLED':
        return <XCircle className="h-3 w-3" />
      case 'PENDING':
        return <Clock className="h-3 w-3" />
      default:
        return <Shield className="h-3 w-3" />
    }
  }

  // Get claim status icon
  const getClaimStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'PAID':
        return <CheckCircle className="h-3 w-3" />
      case 'APPROVED':
        return <CheckCircle className="h-3 w-3" />
      case 'PENDING':
      case 'UNDER_REVIEW':
        return <Clock className="h-3 w-3" />
      case 'REJECTED':
        return <XCircle className="h-3 w-3" />
      default:
        return <FileText className="h-3 w-3" />
    }
  }

  // Get user's display name for welcome message
  const getUserDisplayName = () => {
    try {
      const storedUser = localStorage.getItem('user')
      if (storedUser) {
        const userData = JSON.parse(storedUser)
        if (userData?.user_metadata?.first_name) {
          return userData.user_metadata.first_name
        }
        if (userData?.first_name) {
          return userData.first_name
        }
        if (userData?.email) {
          return userData.email.split('@')[0]
        }
      }
    } catch (error) {
      console.error('Error parsing stored user:', error)
    }
    return 'Customer'
  }

  // Handle policy click to navigate to policy details page
  const handlePolicyClick = (policy) => {
    const newUrl = `/customer/policy-details?user_id=${userId}&policy_id=${policy.policy_id}`
    window.location.href = newUrl
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Welcome Message */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Home className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Welcome back, {getUserDisplayName()}! 👋
              </h1>
              <div className="flex items-center space-x-2 mt-1">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <p className="text-gray-600 text-sm">
                  This is a <span className="font-semibold text-blue-600">mock demo account</span> created for <span className="font-semibold">DevRev</span> demonstration purposes only. 
                  All data shown is simulated for testing and showcase.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div 
          onClick={() => navigate(`/customer/policies?user_id=${userId}`)}
          className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-6 hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-105"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600 mb-1">Active Policies</p>
              <p className="text-3xl font-bold text-blue-900">{totalPolicies}</p>
              <p className="text-xs text-blue-600 mt-1">Coverage active</p>
            </div>
            <div className="p-3 bg-blue-200 rounded-full">
              <CreditCard className="h-6 w-6 text-blue-700" />
            </div>
          </div>
        </div>

        <div 
          onClick={() => navigate(`/customer/claims?user_id=${userId}`)}
          className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-6 hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-105"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600 mb-1">Total Claims</p>
              <p className="text-3xl font-bold text-green-900">{totalClaims}</p>
              <p className="text-xs text-green-600 mt-1">Claims submitted</p>
            </div>
            <div className="p-3 bg-green-200 rounded-full">
              <FileText className="h-6 w-6 text-green-700" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-6 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-orange-600 mb-1">Total Coverage</p>
              <p className="text-3xl font-bold text-orange-900">
                ₹{policies.reduce((sum, policy) => sum + (policy.sum_insured || 0), 0).toLocaleString()}
              </p>
              <p className="text-xs text-orange-600 mt-1">Sum insured</p>
            </div>
            <div className="p-3 bg-orange-200 rounded-full">
              <Shield className="h-6 w-6 text-orange-700" />
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Recent Policies */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900">Recent Policies</h2>
          <button 
            onClick={() => {
              // Navigate to policies page with a parameter to show new policy form
              navigate(`/customer/policies?user_id=${userId}&action=new`)
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>New Policy</span>
          </button>
        </div>
        
        {policies.length > 0 ? (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {policies.map((policy) => (
                <div 
                  key={policy.policy_id} 
                  onClick={() => handlePolicyClick(policy)}
                  className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all duration-200 hover:scale-105 cursor-pointer"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 text-lg">{policy.policy_number}</h3>
                      <p className="text-sm text-gray-600 font-medium">{policy.policy_type_name}</p>
                    </div>
                    <span className={`px-3 py-1 text-xs font-bold rounded-full flex items-center space-x-1 ${getStatusBadgeColor(policy.policy_status)}`}>
                      {getPolicyStatusIcon(policy.policy_status)}
                      <span>{policy.policy_status}</span>
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Sum Insured:</span>
                      <span className="font-semibold text-gray-900">₹{policy.sum_insured?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Premium:</span>
                      <span className="font-semibold text-gray-900">₹{policy.premium_amount?.toLocaleString()}</span>
                    </div>
                    {policy.policy_start_date && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Start Date:</span>
                        <span className="text-sm text-gray-700">{new Date(policy.policy_start_date).toLocaleDateString()}</span>
                      </div>
                    )}
                    {policy.policy_end_date && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">End Date:</span>
                        <span className="text-sm text-gray-700">{new Date(policy.policy_end_date).toLocaleDateString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Coverage:</span>
                      <span className="text-sm text-blue-600 font-medium">Full Coverage</span>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <div className="text-center">
                      <span className="text-sm text-blue-600 font-medium">Click to view details →</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="bg-blue-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <CreditCard className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Policies Found</h3>
            <p className="text-gray-500 mb-4">You don't have any policies yet. Get started by purchasing a new policy.</p>
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors">
              Browse Policies
            </button>
          </div>
        )}
      </div>

      {/* Enhanced Recent Claims */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900">Recent Claims</h2>
          <button 
            onClick={() => {
              openChatWithContext(
                'I would like to file a new claim. Can you help me with the claim submission process and required documents?',
                'dashboard_new_claim'
              )
            }}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>New Claim</span>
          </button>
        </div>
        
        {claims.length > 0 ? (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {claims.map((claim) => (
                <div 
                  key={claim.claim_id} 
                  className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all duration-200 hover:scale-105 cursor-pointer"
                  onClick={() => navigate(`/customer/claims/${claim.claim_id}?user_id=${userId}`)}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 text-lg">{claim.claim_number}</h3>
                      <p className="text-sm text-gray-600 font-medium">{claim.claim_type || 'Health Claim'}</p>
                    </div>
                    <span className={`px-3 py-1 text-xs font-bold rounded-full flex items-center space-x-1 ${getStatusBadgeColor(claim.claim_status)}`}>
                      {getClaimStatusIcon(claim.claim_status)}
                      <span>{claim.claim_status}</span>
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Amount:</span>
                      <span className="font-semibold text-gray-900">₹{claim.total_bill_amount?.toLocaleString()}</span>
                    </div>
                    {claim.approved_amount && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Approved:</span>
                        <span className="font-semibold text-green-600">₹{claim.approved_amount.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Date:</span>
                      <span className="text-sm text-gray-700">{new Date(claim.incident_date || claim.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="bg-green-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <FileText className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Claims Found</h3>
            <p className="text-gray-500 mb-4">You haven't submitted any claims yet. Submit your first claim to get started.</p>
            <button className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-colors">
              Submit Claim
            </button>
          </div>
        )}
      </div>

      {/* Network Hospitals - City-based */}
      <CityHospitalsSection />

      {/* Enhanced Help Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-3 bg-blue-100 rounded-full">
            <HelpCircle className="h-6 w-6 text-blue-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Need Help?</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a
            href="tel:+918012345678"
            className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4 hover:shadow-md transition-all duration-200 hover:scale-105 cursor-pointer block"
          >
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-200 rounded-full">
                <Phone className="h-5 w-5 text-green-700" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Call Support</p>
                <p className="text-sm text-green-700 font-medium">+91 80 1234 5678</p>
                <p className="text-xs text-green-600">24/7 Available</p>
              </div>
            </div>
          </a>
          <a
            href={`https://mail.google.com/mail/?view=cm&to=insurance.devrev@gmail.com&su=Support Request&body=Hello,%0D%0A%0D%0AI need assistance with my insurance.%0D%0A%0D%0AFrom: ${user?.email || 'customer'}%0D%0AUser ID: ${userId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4 hover:shadow-md transition-all duration-200 hover:scale-105 cursor-pointer block"
          >
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-200 rounded-full">
                <Mail className="h-5 w-5 text-blue-700" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Email Support</p>
                <p className="text-sm text-blue-700 font-medium">insurance.devrev@gmail.com</p>
                <p className="text-xs text-blue-600">Response within 2 hours</p>
              </div>
            </div>
          </a>
          <button
            onClick={() => openChatWithContext(
              `Hi, I need help with my insurance. My email is ${user?.email || 'customer'}. Can you assist me?`,
              'dashboard_help_section'
            )}
            className="bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 rounded-lg p-4 hover:shadow-md transition-all duration-200 hover:scale-105 cursor-pointer text-left"
          >
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-yellow-200 rounded-full">
                <MessageCircle className="h-5 w-5 text-yellow-700" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Talk to an Advisor</p>
                <p className="text-sm text-yellow-700 font-medium">Chat with us instantly</p>
                <p className="text-xs text-yellow-600">Available 24/7</p>
              </div>
            </div>
          </button>
        </div>
      </div>

    </div>
  )
}

export default CustomerDashboard